/**
 * Hindi breadcrumb trails: the Hindi name of every /hi/ route and section leaf.
 *
 * Kept out of the root layout's client graph on purpose: these maps are
 * several KB of Devanagari that only /hi/ pages need, and AutoBreadcrumbs
 * loads this module through a dynamic import on those pages only
 * (tests/root-client-graph-budget.test.ts denylists it).
 */

import {
  HINDI_HOME_CRUMB,
  RASHI_NAMES_HI,
  compatibilityPairName,
  titleCase,
  type BreadcrumbItem,
} from "./breadcrumbs-base";

/**
 * The Hindi name of every top-level Hindi route. The visible trail
 * (`AutoBreadcrumbs`) and the BreadcrumbList JSON-LD (`breadcrumbSchema` in
 * `schema/core.ts`) both read this, so a Hindi page names itself the same way
 * in both — and in Hindi, not in a title-cased English slug.
 * `breadcrumbs.test.ts` fails when a route under src/app/hi has no entry.
 */
export const HI_PAGE_NAMES: Readonly<Record<string, string>> = {
  "aaj-ka-rashifal": "आज का राशिफल",
  amavasya: "अमावस्या",
  "ascendant-calculator": "लग्न कैलकुलेटर",
  ashtakvarga: "अष्टकवर्ग",
  astrologers: "AI ज्योतिषी",
  "baby-names": "शिशु नाम",
  "bhagyank-calculator": "भाग्यांक कैलकुलेटर",
  "birth-time-rectification": "जन्म समय शोधन",
  "career-ke-liye-jyotish": "करियर के लिए ज्योतिष",
  "career-report": "करियर रिपोर्ट",
  "char-dasha": "चर दशा",
  choghadiya: "चौघड़िया",
  compatibility: "राशि मेल",
  dasamsa: "दशमांश कुंडली (D10)",
  "dasha-calculator": "दशा कैलकुलेटर",
  "dosha-remedy-plan": "दोष निवारण योजना",
  dwadasamsa: "द्वादशांश कुंडली (D12)",
  ekadashi: "एकादशी",
  "free-kundali": "जन्म कुंडली",
  gemstones: "रत्न",
  graha: "नवग्रह",
  "gun-milan": "गुण मिलान",
  "jyotishi-se-baat-karen": "ज्योतिषी से बात करें",
  "jyotishi-se-chat": "ज्योतिषी से चैट",
  "kaal-sarp-dosha": "काल सर्प दोष",
  "kp-astrology": "केपी ज्योतिष",
  "kundli-matching": "कुंडली मिलान",
  "lal-kitab-kundli": "लाल किताब कुंडली",
  "lal-kitab-remedies": "लाल किताब उपाय",
  mahadasha: "महादशा",
  "mangal-dosha-calculator": "मंगल दोष जांच",
  "manglik-calculator": "मांगलिक कैलकुलेटर",
  mantras: "ग्रह मंत्र",
  "marriage-report": "विवाह रिपोर्ट",
  "masik-shivratri": "मासिक शिवरात्रि",
  "mercury-retrograde": "बुध वक्री",
  "monthly-rashifal": "मासिक राशिफल",
  "muft-jyotish-chat": "मुफ्त ज्योतिष चैट",
  muhurat: "शुभ मुहूर्त",
  "mulank-calculator": "मूलांक कैलकुलेटर",
  "nadi-astrology": "नाड़ी ज्योतिष",
  "nadi-dosh": "नाड़ी दोष",
  nakshatra: "नक्षत्र",
  "nakshatra-calculator": "नक्षत्र कैलकुलेटर",
  "name-numerology": "नाम अंक",
  "navamsa-chart": "नवांश कुंडली (D9)",
  numerology: "अंक ज्योतिष",
  panchang: "पंचांग",
  pandits: "AI पंडित जी",
  "pitra-dosha": "पितृ दोष",
  pradosh: "प्रदोष व्रत",
  "prashna-kundali": "प्रश्न कुंडली",
  privacy: "गोपनीयता नीति",
  purnima: "पूर्णिमा",
  "rahu-kaal": "राहु काल",
  rashi: "राशि",
  "rashi-calculator": "राशि कैलकुलेटर",
  rashifal: "राशिफल",
  remedies: "ज्योतिष उपाय",
  "sade-sati": "साढ़े साती",
  "sankashti-chaturthi": "संकष्टी चतुर्थी",
  sankranti: "संक्रांति",
  saptamsa: "सप्तांश कुंडली (D7)",
  "saturn-transit": "शनि गोचर",
  "shaadi-ke-liye-jyotish": "शादी के लिए ज्योतिष",
  shadbala: "षड्बल",
  shashtiamsa: "षष्ट्यंश कुंडली (D60)",
  "shubh-muhurat": "शुभ मुहूर्त",
  "tara-balam": "तारा बल",
  "tithi-today": "आज की तिथि",
  transits: "ग्रह गोचर",
  trimsamsa: "त्रिंशांश कुंडली (D30)",
  upay: "ज्योतिष उपाय",
  "vastu-shastra": "वास्तु शास्त्र",
  "venus-transit": "शुक्र गोचर",
  "vrat-calendar": "व्रत कैलेंडर",
  "weekly-rashifal": "साप्ताहिक राशिफल",
  widgets: "विजेट",
  yoga: "योग",
  "yogini-dasha": "योगिनी दशा",
};

/**
 * Hindi sections whose index is a served page, so a page nested under one
 * (`/hi/mantras/guru/`, `/hi/mahadasha/ketu/mars-antardasha/`) links it as
 * the middle crumb. A section missing here would print a dead parent link.
 */
const HI_SECTION_HUBS: ReadonlySet<string> = new Set([
  "choghadiya",
  "gemstones",
  "graha",
  "lal-kitab-remedies",
  "mahadasha",
  "mantras",
  "muhurat",
  "nakshatra",
  "panchang",
  "rahu-kaal",
  "shubh-muhurat",
  "vastu-shastra",
  "yoga",
]);

/** Hindi names for the nested slugs a title-cased English word would misname. */
const HI_LEAF_NAMES: Readonly<Record<string, string>> = {
  today: "आज",
  tomorrow: "कल",
  sun: "सूर्य",
  surya: "सूर्य",
  moon: "चंद्र",
  chandra: "चंद्र",
  mars: "मंगल",
  mangal: "मंगल",
  mercury: "बुध",
  budh: "बुध",
  jupiter: "गुरु",
  guru: "गुरु",
  venus: "शुक्र",
  shukra: "शुक्र",
  saturn: "शनि",
  shani: "शनि",
  rahu: "राहु",
  ketu: "केतु",
};

/**
 * Hindi leaf names for the nested pages of one section, where the slug is an
 * English word (`/hi/gemstones/blue-sapphire/`, `/hi/vastu-shastra/kitchen/`).
 * Each name is the one the page's own BreadcrumbList caller passes (gemstones:
 * `HI_STONE_DATA[slug].hindiName`; vastu rooms: `${displayHi} वास्तु` from
 * `VASTU_ROOMS`), and `breadcrumbs.test.ts` pins each map to that source. The
 * sources are not imported: the gemstone data lives under src/app, and the
 * vastu data is ~1,200 lines of page copy this client and edge-worker module
 * would otherwise ship.
 */
const HI_SECTION_LEAF_NAMES: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  gemstones: {
    ruby: "माणिक",
    pearl: "मोती",
    "red-coral": "मूंगा",
    emerald: "पन्ना",
    "yellow-sapphire": "पुखराज",
    diamond: "हीरा",
    "blue-sapphire": "नीलम",
    hessonite: "गोमेद",
    "cats-eye": "लहसुनिया",
  },
  "vastu-shastra": {
    kitchen: "रसोई वास्तु",
    bedroom: "शयन कक्ष वास्तु",
    bathroom: "स्नानघर वास्तु",
    "pooja-room": "पूजा कक्ष (मंदिर) वास्तु",
    "living-room": "बैठक / लिविंग रूम वास्तु",
    "study-room": "अध्ययन कक्ष वास्तु",
    "dining-room": "भोजन कक्ष वास्तु",
    "master-bedroom": "मुख्य शयन कक्ष वास्तु",
    "kids-room": "बच्चों का कक्ष वास्तु",
    "guest-room": "अतिथि कक्ष वास्तु",
    balcony: "बालकनी वास्तु",
    "store-room": "भंडार कक्ष वास्तु",
  },
};

/** The Hindi leaf name of `/hi/<section>/<slug>/`, if the resolver knows one. */
function hindiSectionLeafName(section: string, slug: string): string | undefined {
  return HI_SECTION_LEAF_NAMES[section]?.[slug] ?? HI_LEAF_NAMES[slug];
}

export function resolveHindiTrail(path: string): BreadcrumbItem[] | null {
  const rest = path.slice(4); // strip "/hi/"
  const segments = rest.split("/").filter(Boolean);
  const trail: BreadcrumbItem[] = [HINDI_HOME_CRUMB];

  if (segments.length === 0) return trail;

  const [first, second, third] = segments;

  if (first === "rashifal") return resolveHindiRashifalTrail(trail, second);
  if (first === "rashi") return resolveHindiRashiTrail(trail, second, third);
  if (first === "compatibility") return resolveHindiCompatibilityTrail(trail, second);

  if (first === "calendar") {
    // /hi/calendar/ itself is the English calendar; the Hindi festival
    // directory is the page under it.
    trail.push({ name: "व्रत और त्योहार" });
    return trail;
  }

  const hiName = HI_PAGE_NAMES[first] ?? titleCase(first.replace(/-/g, " "));
  if (second && HI_SECTION_HUBS.has(first)) {
    trail.push({ name: hiName, href: `/hi/${first}/` });
    // A slug with no Hindi name falls back to the title-cased slug; the
    // BreadcrumbList then keeps the page's own Hindi name for it
    // (`breadcrumbSchema`), and D1 rows name the leaf from their title.
    trail.push({
      name: hindiSectionLeafName(first, second) ?? titleCase(second.replace(/-/g, " ")),
    });
    return trail;
  }
  trail.push({ name: hiName });
  return trail;
}

function resolveHindiRashifalTrail(trail: BreadcrumbItem[], second?: string) {
  trail.push({ name: "राशिफल", href: "/hi/aaj-ka-rashifal/" });
  if (second && RASHI_NAMES_HI[second]) trail.push({ name: RASHI_NAMES_HI[second] });
  return trail;
}

function resolveHindiRashiTrail(trail: BreadcrumbItem[], second?: string, third?: string) {
  trail.push({ name: "राशि", href: "/hi/rashi/" });
  if (second) {
    trail.push({
      name: RASHI_NAMES_HI[second] || titleCase(second),
      href: `/hi/rashi/${second}/`,
    });
    if (third) trail.push({ name: titleCase(third) });
  }
  return trail;
}

function resolveHindiCompatibilityTrail(trail: BreadcrumbItem[], second?: string) {
  trail.push({ name: "राशि मेल", href: "/hi/compatibility/" });
  if (second) trail.push({ name: compatibilityPairName(second, RASHI_NAMES_HI) });
  return trail;
}
